import { api } from '../../../servicesGlobais/api'
import { OpcaoMenu, CriarOpcaoMenuDto, AtualizarOpcaoMenuDto } from '../tipos/menu'
import { extractErrorMessage } from '../../../utils/formatUtils'

function normalizeOpcaoMenu(backend: any): OpcaoMenu {
  return {
    codigo: Number(backend.codigo ?? backend.Codigo ?? 0),
    titulo: backend.titulo ?? backend.Titulo ?? '',
    rota: backend.rota ?? backend.Rota ?? '',
    icone: backend.icone ?? backend.Icone,
    parentCodigo: backend.parentCodigo ?? backend.ParentCodigo ? Number(backend.parentCodigo ?? backend.ParentCodigo) : undefined,
    ordem: Number(backend.ordem ?? backend.Ordem ?? 0),
    ativo: backend.ativo ?? backend.Ativo ?? true,
    recurso: backend.recurso ?? backend.Recurso,
  }
}

export const opcaoMenuService = {
  async obterTodas(): Promise<OpcaoMenu[]> {
    try {
      const opcoes = await api.get<any[]>('/OpcaoMenu')
      return Array.isArray(opcoes) ? opcoes.map(normalizeOpcaoMenu) : []
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar opções de menu'))
    }
  },

  async obterPorCodigo(codigo: number): Promise<OpcaoMenu> {
    try {
      const opcao = await api.get<any>(`/OpcaoMenu/${codigo}`)
      return normalizeOpcaoMenu(opcao)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar opção de menu'))
    }
  },

  async criar(data: CriarOpcaoMenuDto): Promise<OpcaoMenu> {
    try {
      const opcao = await api.post<any>('/OpcaoMenu', data)
      return normalizeOpcaoMenu(opcao)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao criar opção de menu'))
    }
  },

  async atualizar(codigo: number, data: AtualizarOpcaoMenuDto): Promise<OpcaoMenu> {
    try {
      const opcao = await api.put<any>(`/OpcaoMenu/${codigo}`, data)
      return normalizeOpcaoMenu(opcao)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao atualizar opção de menu'))
    }
  },

  async excluir(codigo: number): Promise<void> {
    try {
      await api.delete(`/OpcaoMenu/${codigo}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao excluir opção de menu'))
    }
  },

  /** Papéis que têm permissão de leitura para o recurso desta opção (quem vê este item). */
  async obterPapeisComAcesso(codigo: number): Promise<{ codigo: number; nome: string }[]> {
    const res = await api.get<any[]>(`/OpcaoMenu/${codigo}/papeis-com-acesso`)
    const list = Array.isArray(res) ? res : []
    return list.map((p: any) => ({
      codigo: Number(p.codigo ?? p.Codigo ?? 0),
      nome: p.nome ?? p.Nome ?? '',
    }))
  },

  /** Lista menus com recurso para tela de permissões por menu (cada um com papeisComAcesso). */
  async obterParaPermissoes(): Promise<MenuParaPermissao[]> {
    const res = await api.get<any[]>('/OpcaoMenu/para-permissoes')
    const list = Array.isArray(res) ? res : []
    return list.map((m: any) => ({
      codigo: Number(m.codigo ?? m.Codigo ?? 0),
      titulo: m.titulo ?? m.Titulo ?? '',
      rota: m.rota ?? m.Rota ?? '',
      recurso: m.recurso ?? m.Recurso ?? '',
      papeisComAcesso: Array.isArray(m.papeisComAcesso)
        ? m.papeisComAcesso.map((p: any) => ({
            codigo: Number(p.codigo ?? p.Codigo ?? 0),
            nome: p.nome ?? p.Nome ?? '',
          }))
        : [],
    }))
  },

  /** Atualiza quais papéis podem acessar este item de menu (permissões por menu). */
  async atualizarPermissoes(codigo: number, papelCodigos: number[]): Promise<void> {
    await api.put(`/OpcaoMenu/${codigo}/permissoes`, { papelCodigos })
  },
}

export interface MenuParaPermissao {
  codigo: number
  titulo: string
  rota: string
  recurso: string
  papeisComAcesso: { codigo: number; nome: string }[]
}
